import React from 'react';
import { Link } from 'react-router-dom';
import Tag from 'antd/lib/tag';
import Text from 'antd/lib/typography/Text';
import { WarningOutlined } from '@ant-design/icons';

import LibraryThumb from 'components/annotation-page/workspace/references/library-thumb';
import { previewUrl } from 'components/annotation-page/workspace/references/reference-model';
import type { Scene } from 'components/annotation-page/workspace/scene/scene-api';
import { requestScenePanel } from 'components/annotation-page/workspace/scene/scene-context';
import { NoModelThumb } from 'components/annotation-page/workspace/scene/scene-picker';
import {
    explains, isFloor, ROOMS_STEP, rowName, rowTag, SceneRow, sceneRows, unusedNote,
} from 'components/annotation-page/workspace/scene/scene-model';
import { t } from 'utils/i18n';

interface Props {
    taskId: number;
    // the editor of the task's first job
    workspace: string;
    scene: Scene | null;
    failed: boolean;
    backendAPI: string;
}

function Tile({ row, scene, backendAPI }: { row: SceneRow, scene: Scene, backendAPI: string }): JSX.Element {
    const tag = rowTag(row);
    return (
        <div className={`cvat-task-scene-tile cvat-task-scene-tile-${row.status}`}>
            {row.status === 'none' ? <NoModelThumb className='cvat-task-scene-ball' /> : (
                <LibraryThumb
                    className='cvat-task-scene-ball'
                    src={previewUrl(row.preview ?? undefined, backendAPI)}
                    fullSrc={previewUrl(row.preview ?? undefined, backendAPI, true)}
                />
            )}
            <div className='cvat-task-scene-tile-text'>
                <Text strong>{t(row.title)}</Text>
                {explains(row) ? (
                    <Text type='secondary'>{rowName(row, scene.mode)}</Text>
                ) : (
                    <Text type='secondary' ellipsis={{ tooltip: rowName(row, scene.mode) }}>
                        {rowName(row, scene.mode)}
                    </Text>
                )}
                {unusedNote(row, scene.mode) && <Text type='secondary'>{unusedNote(row, scene.mode)}</Text>}
            </div>
            {tag && <Tag color={tag.color}>{tag.text}</Tag>}
        </div>
    );
}

// The scene settings in the saved mode, read-only like the other settings of the page;
// materials are set in the editor's Scene panel
export default function SceneSection({
    taskId, workspace, scene, failed, backendAPI,
}: Props): JSX.Element {
    const rows = scene ? sceneRows(scene) : [];
    const attention = rows.filter((row) => row.status === 'fallback' || row.status === 'none');
    const floors = rows.filter((row) => isFloor(row.id));
    return (
        <section aria-label={t('Scene')} className='cvat-task-page-section'>
            <div className='cvat-task-page-section-head'>
                <h2>{t('Scene')}</h2>
                {scene && (
                    <Text type='secondary'>
                        {`${scene.mode} · ${attention.length ?
                            t('{n} need attention', { n: attention.length }) : t('nothing on a fallback')}`}
                    </Text>
                )}
                <span className='cvat-task-page-spacer' />
                <Link to={workspace} onClick={() => requestScenePanel(taskId)}>{t('Set up in the editor →')}</Link>
            </div>
            {!scene && !failed && <div className='cvat-task-scene-skeleton cvat-preview-skeleton' aria-busy />}
            {failed && <Text type='secondary'>{t('Could not load the scene.')}</Text>}
            {attention.length > 0 && (
                <Text type='warning' className='cvat-task-scene-warning'>
                    <WarningOutlined />
                    {t('On a fallback or without a model: {items}', {
                        items: attention.map((row) => t(row.title)).join(', '),
                    })}
                </Text>
            )}
            {scene && (
                <>
                    <Text strong>{t('Materials')}</Text>
                    <div className='cvat-task-scene-tiles'>
                        {rows.filter((row) => row.kind === 'material' && !isFloor(row.id)).map((row) => (
                            <Tile key={row.id} row={row} scene={scene} backendAPI={backendAPI} />
                        ))}
                    </div>
                    {floors.length > 0 ? (
                        <div className='cvat-task-scene-floors' aria-label={t('Floors')}>
                            {floors.map((row) => (
                                <div
                                    key={row.id}
                                    className={`cvat-task-scene-floor cvat-task-scene-tile-${row.status}`}
                                    title={rowName(row, scene.mode)}
                                >
                                    <LibraryThumb
                                        className='cvat-task-scene-ball'
                                        src={previewUrl(row.preview ?? undefined, backendAPI)}
                                        fullSrc={previewUrl(row.preview ?? undefined, backendAPI, true)}
                                    />
                                    <span>{t(row.title)}</span>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <Text type='secondary'>
                            {t('No rooms on the drawings: run “{step}” to get the floors', { step: t(ROOMS_STEP) })}
                        </Text>
                    )}
                    <Text strong>{t('Models')}</Text>
                    <div className='cvat-task-scene-tiles'>
                        {rows.filter((row) => row.kind === 'model').map((row) => (
                            <Tile key={row.id} row={row} scene={scene} backendAPI={backendAPI} />
                        ))}
                    </div>
                </>
            )}
        </section>
    );
}
